// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { DataCache } from '@shared/commons/DataCache';
import OEQuote1ViewerTemplate from '@front/templates/OEQuote1ViewerTemplate.html';
import { HistoryType } from '@shared/utils/History/HistoryType';
import { Control } from '../../elements/Control';
import { SnapshotScreen } from '../../screen/SnapshotScreen';
import { WDSettings } from '../../settings/WDGeneralSettings';
import { ControlsTypes } from '../../UtilsClasses/FactoryConstants';
import { StringUtils } from '@shared/utils/StringUtils';
import { Account } from '@shared/commons/cache/Account';
export class OEQuote1Viewer extends Control {
    constructor() {
        super();
        this.completed = false;
    }
    getType() {
        return ControlsTypes.OEQuote1Viewer;
    }
    oninit() {
        super.oninit();
        super.observe('instrument', this.onInstrumentChanged);
        super.observe('account quote', this.updateValues);
        super.observe('snapshotBtnDisabled', this.onUpdateSnapshotBtnEnability, { init: false });
        super.on('OnGetSnapshotClick', this.onGetSnapshotClick);
        WDSettings.settingsChanged.Subscribe(this.updateReverseView, this);
        Account.TradeStatusChanged.Subscribe(this.updateSnapshotBtnEnability, this); // обновление enability кнопок Get snapshot при изменениях значения на сервере
        this.updateReverseView();
    }
    oncomplete() {
        super.oncomplete();
        this.completed = true;
        void this.set('snapshotBtnDisabled', DataCache.getAvailableNumberOfSnapshotRequests() === 0);
        this.onInstrumentChanged(super.get('instrument'), null);
    }
    onInstrumentChanged(newInstrument, oldInstrument) {
        if (newInstrument == null || newInstrument === oldInstrument || !this.completed) {
            return;
        }
        this.unsubscribeQuotes(oldInstrument);
        this.subscribeQuotes(newInstrument);
    }
    updateValues() {
        const ins = super.get('instrument');
        const account = super.get('account');
        const quote = super.get('quote');
        const na = StringUtils.NOT_AVAILABLE;
        if (ins == null || account == null || quote == null) {
            void super.set({ bid: na, ask: na, spread: na });
            return;
        }
        const sp = DataCache.GetSpreadPlan(account);
        const bid = quote.BidSpread_SP_Ins(sp, ins);
        const ask = quote.AskSpread_SP_Ins(sp, ins);
        const spread = ins.Level1.GetSpread(account);
        void super.set({
            bid: isNaN(bid) ? na : ins.formatPrice(bid),
            ask: isNaN(ask) ? na : ins.formatPrice(ask),
            spread: isNaN(spread) ? na : Math.round(spread).toString()
        });
    }
    dispose() {
        WDSettings.settingsChanged.UnSubscribe(this.updateReverseView, this);
        Account.TradeStatusChanged.UnSubscribe(this.updateSnapshotBtnEnability, this);
        this.unsubscribeQuotes(super.get('instrument'));
        super.dispose();
    }
    subscribeQuotes(instrument) {
        if (instrument == null) {
            return;
        }
        this.newQuote(instrument.LastQuote);
        DataCache.FQuoteCache.addListener(instrument, this, HistoryType.QUOTE_LEVEL1);
    }
    unsubscribeQuotes(instrument) {
        if (instrument == null) {
            return;
        }
        DataCache.FQuoteCache.removeListener(instrument, this, HistoryType.QUOTE_LEVEL1);
        this.newQuote(null);
    }
    newQuote(quote) {
        if (quote != null && quote.Type === HistoryType.QUOTE_INSTRUMENT_DAY_BAR) {
            return;
        }
        void super.set('quote', quote);
    }
    updateReverseView() {
        void super.set('reverseView', WDSettings.reverseButtonsOrder);
    }
    onGetSnapshotClick() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.set('snapshotBtnDisabled', true);
            const ins = this.get('instrument');
            if (!isNullOrUndefined(ins)) {
                yield SnapshotScreen.showAsync(ins);
            }
            this.updateSnapshotBtnEnability();
        });
    }
    updateSnapshotBtnEnability() {
        void this.set('snapshotBtnDisabled', DataCache.getAvailableNumberOfSnapshotRequests() === 0);
    }
    onUpdateSnapshotBtnEnability(newValue, oldValue) {
        if (!isNullOrUndefined(newValue) && newValue !== oldValue) {
            const tooltip = 'panel.newOrderEntry.snapshotBtn.' + (newValue ? 'disabled' : 'enabled') + '.tt';
            void this.set('snapshotBtnTooltip', tooltip);
        }
    }
}
Control.extendWith(OEQuote1Viewer, {
    template: OEQuote1ViewerTemplate,
    data: function () {
        return {
            NA: StringUtils.NOT_AVAILABLE,
            bid: '',
            ask: '',
            spread: '',
            quote: null,
            instrument: null,
            account: null,
            delayedVisible: false,
            snapshotVisible: false,
            delayedText: '',
            snapshotText: '',
            snapshotBtnTooltip: '',
            snapshotBtnDisabled: false,
            reverseView: false
        };
    }
});
